import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { TopBar } from "@/components/layout/TopBar";
import { PARTNER } from "@/constants/testIds";
import { toast } from "sonner";
import { Building2, Copy, Mail, Users, Clock } from "lucide-react";

export default function PartnerConsole() {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const [data, setData] = useState(null);
  const [invitations, setInvitations] = useState([]);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const roles = new Set(user?.roles || []);
  const isPartner = roles.has("partner_org") || roles.has("admin");

  const load = async () => {
    try {
      const [d, inv] = await Promise.all([
        api.get("/partner/dashboard"),
        api.get("/partner/invitations"),
      ]);
      setData(d.data);
      setInvitations(inv.data.items || []);
    } catch (e) {
      // pending approval scenario
      if (e?.response?.status !== 423) {
        try {
          const d = await api.get("/partner/dashboard").catch(() => null);
          if (d) setData(d.data);
        } catch {}
      }
    }
  };

  useEffect(() => { if (isPartner) load(); }, [isPartner]);

  const createInvite = async () => {
    setBusy(true);
    try {
      const { data: inv } = await api.post("/partner/invitations", { email: email || undefined, ttl_days: 14 });
      const url = `${window.location.origin}/register?invite=${inv.token}`;
      await navigator.clipboard.writeText(url).catch(() => {});
      toast.success(lang === "fr" ? "Lien copié dans le presse-papier" : "Link copied to clipboard");
      setEmail("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const copyLink = async (token) => {
    const url = `${window.location.origin}/register?invite=${token}`;
    await navigator.clipboard.writeText(url).catch(() => {});
    toast.success(lang === "fr" ? "Lien copié" : "Link copied");
  };

  if (!isPartner) {
    return (
      <div>
        <TopBar title="Partner Console" subtitle={lang === "fr" ? "Accès réservé aux organismes partenaires" : "Restricted to partner organisations"} />
        <div className="px-6 md:px-10 py-8">
          <div className="rounded-md border border-[#E4E1D8] bg-white p-8 text-center">
            <Building2 className="h-6 w-6 text-[#0B2545] mx-auto mb-4" strokeWidth={1.5} />
            <div className="text-sm text-[#1F2937] font-medium">{lang === "fr" ? "Votre compte n'est pas rattaché à un organisme partenaire." : "Your account is not attached to a partner organisation."}</div>
            <a href="/partner/register" className="inline-block mt-4 rounded-md border border-[#0B2545]/40 hover:border-[#0B2545] text-[#0B2545] text-sm px-4 py-2 transition-colors">
              {lang === "fr" ? "Enregistrer un organisme" : "Register an organisation"}
            </a>
          </div>
        </div>
      </div>
    );
  }

  const org = data?.organisation;
  const m = data?.metrics || {};

  return (
    <div data-testid={PARTNER.consoleRoot}>
      <TopBar
        title="Partner Console"
        subtitle={org ? `${org.name} · ${(org.country || "").toUpperCase()} ${org.region || ""}`.trim() : lang === "fr" ? "Chargement…" : "Loading…"}
      />
      <div className="px-6 md:px-10 py-8 space-y-8">

        {/* Org status banner */}
        {org && org.status !== "active" && (
          <div className="rounded-md border border-[#D4AF37]/40 bg-[#D4AF37]/5 px-5 py-4 flex items-center gap-3">
            <Clock className="h-4 w-4 text-[#D4AF37]" strokeWidth={1.5} />
            <div className="text-sm text-[#1F2937]">
              {lang === "fr" ? "En attente d'approbation par un Fondateur / Admin." : "Awaiting approval from a Founder / Admin."}
              <span className="ml-2 font-mono text-[10px] uppercase tracking-widest text-[#D4AF37]">
                status: {org.status}
              </span>
            </div>
          </div>
        )}

        {/* Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Metric testId={PARTNER.metricTotal} icon={Users} label={lang === "fr" ? "Bénéficiaires" : "Beneficiaries"} value={m.beneficiaries_total ?? 0} sub={`${m.seat_used ?? 0}/${m.seat_limit ?? 50} seats`} />
          <Metric icon={Users} label={lang === "fr" ? "Actifs 30j" : "Active 30d"} value={m.beneficiaries_active_30d ?? 0} />
          <Metric icon={Users} label={lang === "fr" ? "Score moyen" : "Avg score"} value={m.avg_altiora_score ?? 0} sub="/100" />
          <Metric icon={Mail} label={lang === "fr" ? "Invitations en attente" : "Pending invitations"} value={m.invitations_pending ?? 0} />
        </div>

        {/* Invitation composer */}
        {org?.status === "active" && (
          <div className="rounded-md border border-[#E4E1D8] bg-white p-6">
            <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-[#5B5B62] mb-3">
              {lang === "fr" ? "Inviter un bénéficiaire" : "Invite a beneficiary"}
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                data-testid={PARTNER.inviteEmail}
                type="email" placeholder={lang === "fr" ? "courriel (optionnel)" : "email (optional)"}
                value={email} onChange={(e) => setEmail(e.target.value)}
                className="flex-1 rounded-md bg-[#F8F7F3] border border-[#E4E1D8] focus:border-[#0B2545] focus:ring-1 focus:ring-[#0B2545] outline-none px-3 py-2 text-sm text-[#1F2937]"
              />
              <button
                data-testid={PARTNER.inviteCreate}
                onClick={createInvite} disabled={busy}
                className="rounded-md bg-[#0B2545] hover:bg-[#DCC29A] disabled:opacity-60 text-white px-5 py-2 text-sm font-medium transition-colors inline-flex items-center gap-2"
              >
                <Mail className="h-4 w-4" strokeWidth={2} />
                {busy ? t("common.loading") : (lang === "fr" ? "Générer un lien" : "Generate link")}
              </button>
            </div>
            <div className="mt-2 text-[10px] font-mono text-[#5B5B62]">
              {lang === "fr" ? "Le lien est copié dans votre presse-papier. Partagez-le par le canal de votre choix." : "The link is copied to your clipboard. Share it via any channel."}
            </div>
          </div>
        )}

        {/* Top target roles */}
        {m.top_target_roles?.length > 0 && (
          <div className="rounded-md border border-[#E4E1D8] bg-white p-6">
            <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-[#5B5B62] mb-3">
              {lang === "fr" ? "Métiers les plus ciblés" : "Top target roles"}
            </div>
            <div className="flex flex-wrap gap-2">
              {m.top_target_roles.map((r) => (
                <div key={r.role} className="rounded border border-[#E4E1D8] px-3 py-1.5 text-xs text-[#1F2937]">
                  {r.role} <span className="ml-2 font-mono text-[#0B2545]">×{r.count}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Invitations list */}
        <div className="rounded-md border border-[#E4E1D8] bg-white overflow-hidden">
          <div className="grid grid-cols-[1fr_140px_140px_120px] px-5 py-3 text-[10px] font-mono uppercase tracking-[0.2em] text-[#5B5B62] border-b border-[#E4E1D8]">
            <div>token</div>
            <div>{lang === "fr" ? "courriel" : "email"}</div>
            <div>{lang === "fr" ? "expire" : "expires"}</div>
            <div className="text-right">{lang === "fr" ? "état" : "state"}</div>
          </div>
          {invitations.length === 0 && (
            <div className="px-5 py-10 text-center text-sm text-[#5B5B62]">
              {lang === "fr" ? "Aucune invitation encore." : "No invitations yet."}
            </div>
          )}
          {invitations.map((inv) => (
            <div key={inv.token} data-testid={PARTNER.inviteRow}
                 className="grid grid-cols-[1fr_140px_140px_120px] px-5 py-3 border-b border-[#E4E1D8] last:border-b-0 items-center hover:bg-[#EBE8DE] transition-colors">
              <div className="flex items-center gap-2 min-w-0">
                <span className="font-mono text-[10px] text-[#5B5B62] truncate">{inv.token}</span>
                <button onClick={() => copyLink(inv.token)}
                        className="text-[#5B5B62] hover:text-[#0B2545]" title="copy link">
                  <Copy className="h-3.5 w-3.5" strokeWidth={1.5} />
                </button>
              </div>
              <div className="font-mono text-[10px] text-[#5B5B62] truncate">{inv.email || "—"}</div>
              <div className="font-mono text-[10px] text-[#5B5B62]">{(inv.expires_at || "").slice(0, 10)}</div>
              <div className="text-right">
                {inv.accepted_by ? (
                  <span className="font-mono text-[10px] uppercase tracking-widest text-[#2E8B57] border border-[#2E8B57]/30 rounded px-2 py-0.5">accepted</span>
                ) : (
                  <span className="font-mono text-[10px] uppercase tracking-widest text-[#0B2545] border border-[#0B2545]/25 rounded px-2 py-0.5">pending</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Metric({ icon: Icon, label, value, sub, testId }) {
  return (
    <div data-testid={testId} className="rounded-md border border-[#E4E1D8] bg-white p-5">
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-[#5B5B62]">{label}</div>
        <Icon className="h-4 w-4 text-[#0B2545]" strokeWidth={1.5} />
      </div>
      <div className="mt-4 flex items-baseline">
        <div className="font-mono text-3xl text-[#1F2937] tabular-nums">{value}</div>
        {sub && <div className="ml-2 text-[#5B5B62] font-mono text-xs">{sub}</div>}
      </div>
    </div>
  );
}
